import React, { useEffect, useState } from "react";
import { MessageSquarePlus, MoreVertical, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import LeftDrawer from "../../../components/right-drawer";
import CategorySelect from "../../../components/quick-replies/category-select";
import AddQuickReplyModal from "../../../components/quick-replies/add-modal";
import { loadQuickReplies, saveQuickReplies } from "../../../components/quick-replies/store";
import {
  formatDate,
  QUICK_REPLY_CATEGORIES,
  QuickReply,
  QuickReplyCategory,
  ReplyStatus,
  WEEK_DAYS,
  WeekDay,
} from "../../../components/quick-replies/types";

// ─── StatusBadge ──────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: ReplyStatus }> = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      status === "Active" ? "bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400" : "bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400"
    }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${status === "Active" ? "bg-green-500" : "bg-orange-500"}`} />
    {status}
  </span>
);

// ─── StatusToggle ─────────────────────────────────────────────────────────────

const StatusToggle: React.FC<{ status: ReplyStatus; onChange: (s: ReplyStatus) => void }> = ({ status, onChange }) => {
  const active = status === "Active";
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">Status</label>
      <button
        type="button"
        onClick={() => onChange(active ? "Disabled" : "Active")}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${active ? "bg-indigo-600" : "bg-gray-300 dark:bg-gray-700"}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-900 transition-transform ${active ? "translate-x-6" : "translate-x-1"}`} />
      </button>
      <p className={`mt-1.5 text-sm font-medium ${active ? "text-indigo-600 dark:text-indigo-400" : "text-orange-500 dark:text-orange-400"}`}>{status}</p>
    </div>
  );
};

// ─── QuickRepliesTable ────────────────────────────────────────────────────────

const QuickRepliesTable: React.FC<{
  replies: QuickReply[];
  categories: QuickReplyCategory[];
  onEdit: (reply: QuickReply) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string) => void;
}> = ({ replies, categories, onEdit, onDelete, onToggleStatus }) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("[data-quick-reply-menu]")) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const categoryFor = (value: string) => categories.find((c) => c.value === value);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-left">
            <th className="px-6 py-3 font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">Category Name</th>
            <th className="px-6 py-3 font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">Description</th>
            <th className="px-6 py-3 font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">Quick Reply</th>
            <th className="px-6 py-3 font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">Added By</th>
            <th className="px-6 py-3 font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">Created On</th>
            <th className="px-6 py-3 font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">Status</th>
            <th className="px-6 py-3 font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">Actions</th>
          </tr>
        </thead>
        <tbody>
          {replies.map((reply) => {
            const category = categoryFor(reply.categoryValue);
            return (
              <tr key={reply.id} className="border-b border-gray-100 dark:border-gray-800 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-6 py-4 font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">{category?.label || "—"}</td>
                <td className="px-6 py-4 text-gray-500 dark:text-gray-400 max-w-[220px]">{category?.description || "—"}</td>
                <td className="px-6 py-4 text-gray-800 dark:text-gray-200 max-w-[240px]">{reply.message}</td>
                <td className="px-6 py-4 text-gray-800 dark:text-gray-200 whitespace-nowrap">{reply.addedBy}</td>
                <td className="px-6 py-4 text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatDate(reply.createdOn)}</td>
                <td className="px-6 py-4">
                  <StatusBadge status={reply.status} />
                </td>
                <td className="px-6 py-4 relative">
                  <div data-quick-reply-menu className="relative inline-block">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === reply.id ? null : reply.id)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                    >
                      <MoreVertical size={18} />
                    </button>
                    {openMenuId === reply.id && (
                      <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 py-1">
                        <button
                          onClick={() => {
                            onEdit(reply);
                            setOpenMenuId(null);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            onToggleStatus(reply.id);
                            setOpenMenuId(null);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          {reply.status === "Active" ? "Disable" : "Enable"}
                        </button>
                        <button
                          onClick={() => {
                            onDelete(reply.id);
                            setOpenMenuId(null);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {replies.length === 0 && (
            <tr>
              <td colSpan={7} className="px-6 py-10 text-center text-[#7B7B7B] font-semibold dark:text-gray-500">
                No quick replies yet. Click "Add New Reply" to create one.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ─── EditQuickReplyDrawer ─────────────────────────────────────────────────────

const EditQuickReplyDrawer: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  reply: QuickReply | null;
  categories: QuickReplyCategory[];
  onSave: (reply: QuickReply) => void;
}> = ({ isOpen, onClose, reply, categories, onSave }) => {
  const [draft, setDraft] = useState<QuickReply | null>(reply);

  useEffect(() => {
    setDraft(reply);
  }, [reply]);

  if (!draft) return null;

  const toggleDay = (day: WeekDay) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            schedule: {
              ...prev.schedule,
              customDays: prev.schedule.customDays.includes(day)
                ? prev.schedule.customDays.filter((d) => d !== day)
                : [...prev.schedule.customDays, day],
            },
          }
        : prev
    );
  };

  const handleSave = () => {
    if (!draft.message.trim()) {
      toast.error("Reply message can't be empty");
      return;
    }
    onSave(draft);
    onClose();
  };

  return (
    <LeftDrawer isOpen={isOpen} onClose={onClose} title="Edit Quick Reply" icon={<MessageSquareText size={20} />} width="480px">
      <div className="px-6 py-5 space-y-6">
        <div className="space-y-4">
          <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Reply Settings</p>

          <CategorySelect
            label="Category"
            categories={categories}
            value={draft.categoryValue}
            onChange={(v) => setDraft({ ...draft, categoryValue: v })}
          />

          <div>
            <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">Reply Message</label>
            <textarea
              value={draft.message}
              onChange={(e) => setDraft({ ...draft, message: e.target.value })}
              rows={4}
              className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-transparent rounded-lg px-4 py-3 text-sm text-gray-700 dark:text-gray-300 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Schedule Settings</p>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.schedule.weekdays}
              onChange={(e) => setDraft({ ...draft, schedule: { ...draft.schedule, weekdays: e.target.checked } })}
              className="w-4 h-4 rounded accent-indigo-600"
            />
            Weekdays (Mon-Fri)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.schedule.weekends}
              onChange={(e) => setDraft({ ...draft, schedule: { ...draft.schedule, weekends: e.target.checked } })}
              className="w-4 h-4 rounded accent-indigo-600"
            />
            Weekends (Sat-Sun)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.schedule.useCustomDays}
              onChange={(e) => setDraft({ ...draft, schedule: { ...draft.schedule, useCustomDays: e.target.checked } })}
              className="w-4 h-4 rounded accent-indigo-600"
            />
            Custom Days
          </label>

          {draft.schedule.useCustomDays && (
            <div className="grid grid-cols-3 gap-3 pl-6">
              {WEEK_DAYS.map((day) => (
                <label key={day} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.schedule.customDays.includes(day)}
                    onChange={() => toggleDay(day)}
                    className="w-4 h-4 rounded accent-indigo-600"
                  />
                  {day}
                </label>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">Start Time</label>
              <input
                type="date"
                value={draft.schedule.startTime}
                onChange={(e) => setDraft({ ...draft, schedule: { ...draft.schedule, startTime: e.target.value } })}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-transparent rounded-lg px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">End Time</label>
              <input
                type="date"
                value={draft.schedule.endTime}
                onChange={(e) => setDraft({ ...draft, schedule: { ...draft.schedule, endTime: e.target.value } })}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-transparent rounded-lg px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
          </div>
        </div>

        <StatusToggle status={draft.status} onChange={(s) => setDraft({ ...draft, status: s })} />

        <div>
          <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">Added By</label>
          <input
            readOnly
            value={draft.addedBy}
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 cursor-not-allowed"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">Created On</label>
          <input
            readOnly
            value={formatDate(draft.createdOn)}
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 cursor-not-allowed"
          />
        </div>
      </div>

      <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 px-6 py-4 flex flex-col gap-3">
        <button
          onClick={handleSave}
          className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          Save Changes
        </button>
        <button
          onClick={onClose}
          className="w-full py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Discard Changes
        </button>
      </div>
    </LeftDrawer>
  );
};

// ─── ConversationSettings (main) ──────────────────────────────────────────────

interface ConversationSettingsProps {
  currentUserName?: string;
}

const ConversationSettings: React.FC<ConversationSettingsProps> = ({ currentUserName }) => {
  const [replies, setReplies] = useState<QuickReply[]>(() => loadQuickReplies());
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingReply, setEditingReply] = useState<QuickReply | null>(null);

  const updateReplies = (next: QuickReply[]) => {
    setReplies(next);
    saveQuickReplies(next);
  };

  const handleAdd = (reply: QuickReply) => {
    updateReplies([reply, ...replies]);
    toast.success("Quick reply added");
  };

  const handleSaveEdit = (updated: QuickReply) => {
    updateReplies(replies.map((r) => (r.id === updated.id ? updated : r)));
    toast.success("Quick reply updated");
  };

  const handleDelete = (id: string) => {
    updateReplies(replies.filter((r) => r.id !== id));
    toast.success("Quick reply deleted");
  };

  const handleToggleStatus = (id: string) => {
    updateReplies(replies.map((r) => (r.id === id ? { ...r, status: r.status === "Active" ? "Disabled" : "Active" } : r)));
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Quick Replies</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Setup and manage your quick replies</p>
        </div>
        <button
          onClick={() => setIsAddOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shrink-0"
        >
          Add New Reply
          <MessageSquarePlus size={16} />
        </button>
      </div>

      <QuickRepliesTable
        replies={replies}
        categories={QUICK_REPLY_CATEGORIES}
        onEdit={setEditingReply}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
      />

      <AddQuickReplyModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        categories={QUICK_REPLY_CATEGORIES}
        defaultAddedBy={currentUserName}
        onAdd={handleAdd}
      />

      <EditQuickReplyDrawer
        isOpen={!!editingReply}
        onClose={() => setEditingReply(null)}
        reply={editingReply}
        categories={QUICK_REPLY_CATEGORIES}
        onSave={handleSaveEdit}
      />
    </div>
  );
};

export default ConversationSettings;
